import { apiService } from './api';
import { unwrap } from './serviceUtils';

export type SubscriptionStatus = 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'GRACE_PERIOD' | 'RESTRICTED' | 'CANCELLED';
export type BillingInterval = 'MONTHLY' | 'YEARLY';
export type PlanSlug = 'starter' | 'shop_plus' | 'business_hub';

export type FeatureKey =
  | 'pos'
  | 'inventory'
  | 'expenses'
  | 'basicReports'
  | 'advancedReports'
  | 'purchaseOrders'
  | 'onlineStore'
  | 'onlineCheckout'
  | 'payroll'
  | 'rota'
  | 'vatReturns'
  | 'foodSafety'
  | 'training'
  | 'sync'
  | 'exports';

export type LimitKey = 'maxLocations' | 'maxStaff' | 'maxRegisters' | 'maxProducts';

export type EntitlementValue = boolean | number | string | null;
export type EntitlementMap = Record<string, EntitlementValue>;

/** Error codes returned by the backend when billing blocks a request. */
export const BILLING_ERROR_CODES = {
  /** HTTP 402 — subscription is RESTRICTED/CANCELLED, writes are blocked. */
  SUBSCRIPTION_INACTIVE: 'SUBSCRIPTION_INACTIVE',
  /** HTTP 403 — feature is not part of the current plan. */
  PLAN_UPGRADE_REQUIRED: 'PLAN_UPGRADE_REQUIRED',
  /** HTTP 403 — a numeric plan limit (staff, locations...) has been reached. */
  PLAN_LIMIT_REACHED: 'PLAN_LIMIT_REACHED',
} as const;

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: PlanSlug | string;
  description?: string | null;
  priceMonthly: number | string;
  priceYearly?: number | string | null;
  currency: string;
  billingInterval: BillingInterval;
  features: EntitlementMap;
  isActive: boolean;
  sortOrder: number;
}

export interface BusinessSubscription {
  id: string;
  businessId: string;
  planId: string;
  status: SubscriptionStatus;
  billingInterval: BillingInterval;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  cancelAtPeriodEnd: boolean;
  plan?: SubscriptionPlan;
}

export interface BusinessEntitlements {
  subscription: BusinessSubscription;
  features: EntitlementMap;
  /** RESTRICTED / CANCELLED: reads allowed, writes blocked. */
  isReadOnly: boolean;
  /** TRIALING / ACTIVE / PAST_DUE / GRACE_PERIOD. */
  isAllowed: boolean;
  /** False when ENFORCE_BILLING=false on the server (everything unlocked). */
  enforced: boolean;
}

export interface SubscriptionStatusSummary {
  status: SubscriptionStatus;
  plan: { name: string; slug: string };
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd: boolean;
  isAllowed: boolean;
  isReadOnly: boolean;
  message: string;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  status: string | null;
  total: number; // major units (e.g. 19.00)
  currency: string;
  created: string; // ISO date
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface BillingDetails {
  billingEmail: string | null;
  paymentMethod: { brand: string; last4: string; expMonth: number; expYear: number } | null;
  invoices: BillingInvoice[];
  hasStripeCustomer: boolean;
  hasStripeSubscription: boolean;
}

export type PortalFlow = 'payment_method_update' | 'subscription_cancel' | 'subscription_update';

export const billingApi = {
  async listPlans(): Promise<SubscriptionPlan[]> {
    const response = await apiService.client.get('/api/billing/plans');
    return unwrap<SubscriptionPlan[]>(response);
  },

  /** Get the current user's entitlements. */
  async getEntitlements(): Promise<BusinessEntitlements> {
    const response = await apiService.client.get('/api/billing/me');
    return unwrap<BusinessEntitlements>(response);
  },

  async getStatus(): Promise<SubscriptionStatusSummary> {
    const response = await apiService.client.get('/api/billing/me/status');
    return unwrap<SubscriptionStatusSummary>(response);
  },

  /** OWNER only — invoices, card on file, billing email (from Stripe). */
  async getDetails(): Promise<BillingDetails> {
    const response = await apiService.client.get('/api/billing/me/details');
    return unwrap<BillingDetails>(response);
  },

  /**
   * OWNER only. Returns a Stripe-hosted URL to redirect to.
   * mode = 'checkout' for a new subscription, 'portal' when the business already
   * has a Stripe subscription (plan change confirmed in the Customer Portal).
   */
  async createCheckout(input: { planSlug: PlanSlug; interval: BillingInterval }): Promise<{ url: string; mode: 'checkout' | 'portal' }> {
    const response = await apiService.client.post('/api/billing/checkout', input);
    return unwrap<{ url: string; mode: 'checkout' | 'portal' }>(response);
  },

  /** OWNER only. Call after returning from Checkout with ?session_id= to sync immediately (webhook is the backup). */
  async confirmCheckout(sessionId: string): Promise<BusinessEntitlements> {
    const response = await apiService.client.post('/api/billing/checkout/confirm', { sessionId });
    return unwrap<BusinessEntitlements>(response);
  },

  /** OWNER only. Opens the Stripe Customer Portal (optionally deep-linked into a flow). */
  async createPortal(flow?: PortalFlow): Promise<{ url: string }> {
    const response = await apiService.client.post('/api/billing/portal', flow ? { flow } : {});
    return unwrap<{ url: string }>(response);
  },

  /** Sync latest subscription status directly from Stripe. */
  async syncSubscription(): Promise<BusinessEntitlements> {
    const response = await apiService.client.post('/api/billing/me/sync', {});
    return unwrap<BusinessEntitlements>(response);
  },
};
